import {RunInfo, RunQueueItem} from './types';

export type RunInfoWithProjectAndEntityNames = RunInfo & {
  entityName: string;
  projectName: string;
};

enum RunQueuePopoverItemType {
  RUN = 'RUN',
  RUN_QUEUE_ITEM = 'RUN_QUEUE_ITEM',
}

export interface RunQueuePopoverItem {
  type: RunQueuePopoverItemType;
  id: string; // run.id or rqi.id
  state: string;
  runName?: string;
  runDisplayName?: string;
  associatedRunId?: string | null;
  createdAt: string;
  entityName: string;
  projectName: string;
  queueId?: string;
  queueName?: string;
  jobName?: string;
  runSpecConfigOverrides?: Record<string, any>;
}

/**
 * Formats a timestamp as a relative time string following specific formatting rules:
 * - < 1 minute: "Just now"
 * - < 1 month: "X min ago", "X mins ago", "X hr ago", "X hrs ago", "Yesterday", "X days ago"
 * - > 1 month: "On MMM D" (omits current year)
 */
export const formatRelativeTime = (value: string): string => {
  const date = new Date(value);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Handle future dates or very recent dates
  if (Math.abs(diffInSeconds) < 60) {
    return 'Just now';
  }

  // For past dates only
  if (diffInSeconds > 0) {
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    // < 1 month: X seconds/minutes/hours/days ago
    if (diffInDays < 30) {
      if (diffInMinutes < 60) {
        return diffInMinutes === 1 ? '1 min ago' : `${diffInMinutes} mins ago`;
      }
      if (diffInHours < 24) {
        return diffInHours === 1 ? '1 hr ago' : `${diffInHours} hrs ago`;
      }
      if (diffInDays === 1) {
        return 'Yesterday';
      }
      return `${diffInDays} days ago`;
    }
  }

  // > 1 month or future dates: On MMM D (omit current year)
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const currentYear = now.getUTCFullYear();
  const dateYear = date.getUTCFullYear();
  const month = monthNames[date.getUTCMonth()];
  const day = date.getUTCDate();

  if (dateYear === currentYear) {
    return `On ${month} ${day}`;
  } else {
    return `On ${month} ${day}, ${dateYear}`;
  }
};

// helper to compare dates in descending order
const compareDatesDesc = (dateA: string, dateB: string): number => {
  const timeA = new Date(dateA).getTime();
  const timeB = new Date(dateB).getTime();
  return timeB - timeA;
};

/**
 *
 * This function is used to sort run queue items and run infos in the RunQueueItemsPopover component.
 * It expects a list of (recent) run queue items for the user and a list of the user's recent runs.
 * Currently as of 06/09, recent means the latest 100 runs and run queue items that are pending / queued.
 *
 * We sort RQIs based on three categories in the following order:
 * 1. Pending runs which are LEASED or CLAIMED run queue items without an active run sorted by rqi.createdAt
 * 2. Queued runs which are PENDING run queue items sorted by rqi.createdAt
 * 3. Active runs which come from the user's last 100 runs, sorted by run.createdAt
 */
export const sortUserRunQueueItemsWithRecentRuns = (
  userRunQueueItems: RunQueueItem[],
  userActiveRuns: RunInfoWithProjectAndEntityNames[],
  userRunsNameSet: Set<string>
) => {
  const sortedRunQueueItems = userRunQueueItems.sort((a, b) => {
    // Check if item has a recent run
    const hasRecentRun = (item: RunQueueItem) => {
      if (item.associatedRunId || item.run?.name) {
        const runName = item.run?.name || item.associatedRunId;
        const hasRun = runName && userRunsNameSet.has(runName);

        return hasRun;
      }

      return false;
    };

    const isPendingRun = (rqi: RunQueueItem) =>
      ['LEASED', 'CLAIMED'].includes(rqi.state) && !hasRecentRun(rqi);

    const isQueuedRun = (rqi: RunQueueItem) => rqi.state === 'PENDING';

    const aIsPendingRun = isPendingRun(a);
    const bIsPendingRun = isPendingRun(b);
    const aIsQueuedRun = isQueuedRun(a);
    const bIsQueuedRun = isQueuedRun(b);

    // sort by category first (pending runs -> queued runs)
    if (aIsPendingRun && !bIsPendingRun) {
      return -1;
    }
    if (!aIsPendingRun && bIsPendingRun) {
      return 1;
    }

    if (aIsQueuedRun && !bIsQueuedRun) {
      return -1;
    }
    if (!aIsQueuedRun && bIsQueuedRun) {
      return 1;
    }

    if (aIsPendingRun && bIsPendingRun) {
      return compareDatesDesc(a.createdAt!, b.createdAt!);
    }

    if (aIsQueuedRun && bIsQueuedRun) {
      return compareDatesDesc(a.createdAt!, b.createdAt!);
    }

    // fallback if we somehow get to this codepath
    return compareDatesDesc(
      a.updatedAt || a.createdAt,
      b.updatedAt || b.createdAt
    );
  });

  const sortedActiveRuns = userActiveRuns.sort((a, b) => {
    const aIsRunning = a.state === 'running';
    const bIsRunning = b.state === 'running';

    if (aIsRunning && !bIsRunning) {
      return -1;
    }

    if (!aIsRunning && bIsRunning) {
      return 1;
    }

    return compareDatesDesc(a.createdAt!, b.createdAt!);
  });

  return {
    sortedActiveRuns,
    sortedRunQueueItems,
  };
};

export const mapToPopoverItems = (
  sortedActiveRuns: RunInfoWithProjectAndEntityNames[],
  sortedRunQueueItems: RunQueueItem[],
  queueMap?: Map<string, {id: string; name: string}>,
  runToQueueMap?: Map<
    string,
    {
      id: string;
      name: string;
      jobName?: string;
      runSpecConfigOverrides?: Record<string, any>;
    }
  >
): RunQueuePopoverItem[] => {
  const popoverRunItems = sortedActiveRuns.map(run => {
    const queueInfo = runToQueueMap?.get(run.name);
    return {
      type: RunQueuePopoverItemType.RUN,
      id: run.id,
      state: run.state,
      runName: run.name,
      runDisplayName: run.displayName,
      createdAt: run.createdAt,
      entityName: run.entityName,
      projectName: run.projectName,
      queueId: queueInfo?.id,
      queueName: queueInfo?.name,
      jobName: queueInfo?.jobName,
      runSpecConfigOverrides: queueInfo?.runSpecConfigOverrides ?? {},
    };
  });

  const popoverRunQueueItems = sortedRunQueueItems.map(rqi => {
    const queueInfo = queueMap?.get(rqi.id);
    return {
      type: RunQueuePopoverItemType.RUN_QUEUE_ITEM,
      id: rqi.id,
      state: rqi.displayState,
      associatedRunId: rqi.associatedRunId,
      createdAt: rqi.createdAt,
      entityName: rqi.entityName,
      projectName: rqi.projectName,
      queueId: queueInfo?.id,
      queueName: queueInfo?.name,
      jobName: rqi.jobName,
      runSpecConfigOverrides: rqi.runSpec?.overrides ?? {},
    };
  });

  return [...popoverRunQueueItems, ...popoverRunItems];
};
